import { FormProvider } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Separator } from '~/components';
import { Button } from '~/components/Button';
import { FormFieldCombobox } from '~/components/FormField';
import { FormFieldRadioGroup } from '~/components/FormField/components/FormFieldRadioGroup';
import { useIsExternalUser } from '~/hooks/useIsExternalUser';
import { FEATURE_FLAGS } from '~/modules/experiments/features';
import { useFeatureFlags } from '~/modules/experiments/hooks/useFeatureFlags';
import { Drawer } from '~/modules/settings/components/Drawer';
import {
  EXTERNAL_ROLES,
  INTERNAL_ROLES,
  USER_STATUS,
  USER_TYPES,
} from '~/modules/settings/constants/Filter';
import { useFilterForm } from '~/modules/settings/hooks/useFilterForm';
import type { UsersFilter } from '~/modules/settings/types/filter';
import { translateOptions } from '~/modules/settings/utils/helpers';
import { SelectLabelGroups } from '~settings/components/SelectLabelGroups';
import { SelectLabels } from '~settings/components/SelectLabels';
import { DEFAULT_FILTER_LIMIT } from '~settings/constants';

const defaultValues: UsersFilter = {
  isActive: 'all',
  roles: [],
  labelIds: [],
  isInternal: 'all',
  labelGroupIds: [],
};

interface UsersFilterFormProps {
  open: boolean;
  onApply: () => void;
}

export function UserFormFilter({ open, onApply }: UsersFilterFormProps) {
  const { t } = useTranslation('settings');
  const isCurrentUserInternal = !useIsExternalUser();

  const [labelGroupsEnabled] = useFeatureFlags([
    FEATURE_FLAGS.ContainerSettingsLabelGroups,
  ]);

  const { filterForm, handleApply, handleClear } = useFilterForm<UsersFilter>({
    onApply,
    isOpen: open,
    defaultValues,
  });

  const internalRoles = translateOptions(INTERNAL_ROLES, t);
  const externalRoles = translateOptions(EXTERNAL_ROLES, t);

  const internalFilter = String(filterForm.watch('isInternal'));
  const selectedExternal = internalFilter === 'false';
  const selectedInternal = internalFilter === 'true';

  const getRoleOptions = () => {
    if (!isCurrentUserInternal) {
      return externalRoles;
    }

    switch (internalFilter) {
      case 'true':
        return internalRoles;
      case 'false':
        return externalRoles;
      case 'all':
        return [...internalRoles, ...externalRoles];
      default:
        return [];
    }
  };

  return (
    <FormProvider {...filterForm}>
      <Drawer.Header>
        <Drawer.Title>{t('filters')}</Drawer.Title>
      </Drawer.Header>
      <Drawer.Body>
        <>
          <FormFieldRadioGroup
            name="isActive"
            label={t('userStatus')}
            options={translateOptions(USER_STATUS, t)}
          />
          <Separator className="my-md" />
        </>
        {isCurrentUserInternal ? (
          <>
            <FormFieldRadioGroup
              name="isInternal"
              label={t('userType')}
              options={translateOptions(USER_TYPES, t)}
              onChange={(e) => {
                const value = (e.target as HTMLInputElement).value;

                filterForm.reset({
                  ...defaultValues,
                  isActive: filterForm.getValues('isActive'),
                  isInternal: value,
                });
              }}
            />
            <Separator className="my-md" />
          </>
        ) : null}
        <FormFieldCombobox
          name="roles"
          label={t('dropdowns.role.title', { count: 2 })}
          multiselect={true}
          displaySelectedOptions={true}
          options={getRoleOptions()}
          placeholder={t('dropdowns.role.placeholder', { count: 2 })}
          searchPlaceholder={t('dropdowns.role.searchPlaceholder', {
            count: 2,
          })}
        />
        {!isCurrentUserInternal || !selectedInternal ? (
          <>
            <Separator className="my-md" />
            <SelectLabels
              maxSelectedOptions={DEFAULT_FILTER_LIMIT}
              name="labelIds"
              type="view"
            />
          </>
        ) : null}
        {labelGroupsEnabled && isCurrentUserInternal && !selectedExternal ? (
          <>
            <Separator className="my-md" />
            <SelectLabelGroups
              maxSelectedOptions={DEFAULT_FILTER_LIMIT}
              name="labelGroupIds"
            />
          </>
        ) : null}
      </Drawer.Body>
      <Drawer.Footer>
        <Button
          size="large"
          variant="tertiary"
          onClick={handleClear}
          className="font-normal"
          type="button"
          data-testid="clear-filters-button"
        >
          {t('clear')}
        </Button>
        <Button
          size="large"
          type="button"
          onClick={filterForm.handleSubmit(handleApply)}
          data-testid="apply-filters-button"
        >
          {t('buttons.apply')}
        </Button>
      </Drawer.Footer>
    </FormProvider>
  );
}

UserFormFilter.displayName = 'UserFormFilter';
